/**
 * The status pill on one line of Just added (D222, D224): "Created Project", "Waiting…", "Not sure",
 * "Failed". Where there is something to choose it is a button opening the same small popover the
 * energy die uses (`EnergyBadgeButton`): on a filed line, the other bins to change it to; on a line
 * the AI could not place, the bins to file it as. Waiting lines have nothing to choose, so theirs
 * is a plain label.
 *
 * A menu for the keyboard too: opening it moves focus to the first choice, the arrow keys move
 * between choices, Escape closes it and puts focus back on the pill, and Tab out of it closes it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { AddEntry } from "../justAdded";
import { KINDS, pillOf, type Kind } from "../addResultPill";
import { useDismissablePopover } from "./useDismissablePopover";

export function AddResultPill({
  entry,
  onPick,
  onOpen,
}: {
  entry: AddEntry;
  /** Called with the bin chosen. */
  onPick: (kind: Kind) => void;
  /** Called when the popover opens, so a line about to clear itself stops. */
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissablePopover(open, close, rootRef);

  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
  }, [open]);

  function onMenuKey(event: React.KeyboardEvent) {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [],
    );
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      items[event.key === "Home" ? 0 : items.length - 1]?.focus();
    }
  }
  const pill = pillOf(entry);
  const text = `${pill.label}${pill.verb ? " ▾" : ""}`;

  if (!pill.verb) {
    return <span className={`badge result-pill ${pill.tone}`}>{pill.label}</span>;
  }
  return (
    <span
      className="result-pill-wrap"
      ref={rootRef}
      onBlur={(event) => {
        if (open && !rootRef.current?.contains(event.relatedTarget as Node | null)) close();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`badge result-pill tappable ${pill.tone}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${pill.label}: ${pill.verb.toLowerCase()} another bin`}
        onClick={() => {
          if (!open) onOpen();
          setOpen((v) => !v);
        }}
      >
        {text}
      </button>
      {open && (
        <span
          ref={menuRef}
          className="energy-popover result-popover"
          role="menu"
          aria-label={pill.verb}
          onKeyDown={onMenuKey}
        >
          <span className="small muted">{pill.verb}</span>
          {KINDS.filter((k) => k.kind !== pill.except).map((k) => (
            <button
              key={k.kind}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="btn ghost small"
              onClick={() => {
                setOpen(false);
                onPick(k.kind);
              }}
            >
              {k.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
